import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppDataProvider } from './context/AppDataContext';
import { AudioPlayerProvider } from './context/AudioPlayerContext';
import { PlaylistProvider } from './context/PlaylistContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import Layout from './components/common/Layout';
import HomePage from './pages/HomePage';
import SongDetailsPage from './pages/SongDetailsPage';
import RevisionDetailsPage from './pages/RevisionDetailsPage';
import PlaylistsPage from './pages/PlaylistsPage';
import PlaylistDetailsPage from './pages/PlaylistDetailsPage';
import AddTracksPage from './pages/AddTracksPage';
import LoginPage from './pages/LoginPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth();

  console.log('ProtectedRoute - loading:', loading, 'isAuthenticated:', isAuthenticated, 'user:', user);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    console.log('Not authenticated, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <HomePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/songs/:songId"
        element={
          <ProtectedRoute>
            <Layout>
              <SongDetailsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/songs/:songId/revisions/:revisionId"
        element={
          <ProtectedRoute>
            <Layout>
              <RevisionDetailsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/playlists"
        element={
          <ProtectedRoute>
            <Layout>
              <PlaylistsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/playlists/:playlistId"
        element={
          <ProtectedRoute>
            <Layout>
              <PlaylistDetailsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/playlists/:playlistId/add-tracks"
        element={
          <ProtectedRoute>
            <Layout>
              <AddTracksPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppDataProvider>
            <AudioPlayerProvider>
              <PlaylistProvider>
                <BrowserRouter>
                  <AppRoutes />
                </BrowserRouter>
              </PlaylistProvider>
            </AudioPlayerProvider>
          </AppDataProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
